import axios from 'axios'

/**
 * Resultado de uma requisição HTTP de upload S3.
 * Mantemos minimal: o upload em si só importa se houve erro;
 * o body de sucesso do S3 não traz dados úteis.
 */
export type S3HttpResponse = {
  status: number
  statusText?: string
}

export type S3HttpError = {
  isNetworkError: boolean
  status?: number
  statusText?: string
  cause: unknown
}

export interface S3HttpAdapter {
  put(
    url: string,
    body: Blob | File,
    contentType: string
  ): Promise<S3HttpResponse>
}

/**
 * Adapter padrão para upload S3 via Axios.
 *
 * Por que existe:
 * - O upload S3 usa URL pré-assinada (host externo, sem auth header), então não
 *   passa pelo `ApiClientImplementation` normal — não há necessidade dos
 *   decorators e nem do header Authorization.
 * - Encapsular o `axios` direto aqui (em `infra/`) mantém a camada `service/`
 *   livre de dependências de bibliotecas HTTP, permitindo trocar a
 *   implementação (ex.: `fetch`) sem tocar no service.
 */
export class AxiosS3HttpAdapter implements S3HttpAdapter {
  async put(
    url: string,
    body: Blob | File,
    contentType: string
  ): Promise<S3HttpResponse> {
    try {
      const response = await axios.put(url, body, {
        headers: {
          'Content-Type': contentType
        }
      })

      return {
        status: response.status,
        statusText: response.statusText
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const httpError: S3HttpError = {
          isNetworkError: !error.response,
          status: error.response?.status,
          statusText: error.response?.statusText,
          cause: error
        }
        throw httpError
      }
      throw {
        isNetworkError: false,
        cause: error
      } as S3HttpError
    }
  }
}

export const isS3HttpError = (value: unknown): value is S3HttpError => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'isNetworkError' in value &&
    'cause' in value
  )
}
